/**
 * Schémas Zod — RecruitmentNeed (Sprint 2 V1.0, mai 2026).
 *
 * Source de vérité : docs/specs/recruitment-need.md §3.4.
 *
 * Le besoin de recrutement est l'expression structurée d'une attente
 * entreprise *avant* (ou *sans*) en faire une annonce publique. Il est
 * créé par une entreprise validée via un assistant en 10 étapes.
 *
 * Schémas exposés :
 *   - recruitmentNeedCreateSchema : payload POST création (10 étapes)
 *   - recruitmentNeedUpdateSchema : payload PATCH modification (partial)
 *   - recruitmentNeedFillSchema   : payload POST :id/fill
 *   - recruitmentNeedConvertSchema: payload POST :id/convert (→ JobOffer)
 *   - recruitmentNeedShareSchema  : payload POST :id/share
 *   - recruitmentNeedListSchema   : query GET liste (filtres)
 */
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────────────
// Constantes & types
// ─────────────────────────────────────────────────────────────────────────
export const RECRUITMENT_NEED_STATUS = [
    'draft',
    'active',
    'paused',
    'filled',
    'converted_to_offer',
    'closed',
];
export const RECRUITMENT_URGENCY = ['low', 'normal', 'high', 'urgent'];
export const RECRUITMENT_AVAILABILITY_TYPE = [
    'immediate',
    'fixed_date',
    'school_start',
    'season',
];
/**
 * Types de contrat alignés avec le référentiel HCR V1.0 (cf. CdC V3 §6.4).
 * On ne fige PAS la liste ici par Zod pour rester souple (les codes sont
 * partagés via shared/constants ; le besoin valide juste la forme string).
 */
export const RECRUITMENT_CONTRACT_TYPES = [
    'internship',
    'apprenticeship',
    'professional_training_contract',
    'fixed_term',
    'permanent',
    'extra',
    'seasonal',
    'immersion',
    'freelance',
    'other',
];
/**
 * Famille métier — orientée profil ISTHIA (L3 MHR / L3 IHR / L3 MRC / M1 MHR).
 * Liste révisée V1.1 — gardée compatible BDD (champ String, pas d'enum Postgres).
 * Les valeurs ajoutées en V1.1 : assistant_direction, commercial,
 * project_engineering, other.
 *
 * Ordre : du plus courant côté ISTHIA (direction/management) vers l'opérationnel,
 * puis fonctions support et champ libre.
 */
export const HCR_JOB_FAMILIES = [
    // ── Direction & management ────────────────────────────────────────────────
    'assistant_direction', // Assistant de direction (hôtel, site, restauration…)
    'hotel_management', // Direction / Management hébergement
    'restaurant_management', // Direction / Management restauration
    'collective_catering', // Restauration collective (direction, gestion d'unité)
    // ── Fonctions spécialisées ────────────────────────────────────────────────
    'commercial', // Commercial / Services commerciaux
    'revenue_marketing_sales', // Revenue management / Marketing
    'project_engineering', // Chef de projet / Ingénierie HCR (bureau d'études)
    'tourism_hospitality', // Tourisme / Hospitalité
    'hcr_consulting', // Conseil en management HCR
    // ── Opérationnel encadrement ──────────────────────────────────────────────
    'hotel_front_office', // Réception / Front office
    'hotel_housekeeping', // Étages / Gouvernance
    'service', // Salle / Service en restauration
    'kitchen', // Cuisine
    'bar', // Bar / Café
    // ── Autres ───────────────────────────────────────────────────────────────
    'catering_events', // Traiteur / Événementiel
    'support_admin', // Administration / Support
    'other', // Autre (préciser dans l'intitulé du poste)
];
const CECRL_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
// ─────────────────────────────────────────────────────────────────────────
// CREATE — Assistant 10 étapes
// ─────────────────────────────────────────────────────────────────────────
/**
 * Payload du POST /api/entreprise/besoins. Reflète les 10 étapes de
 * l'assistant `<NeedAssistant>` (cf. spec §5.2).
 *
 * Les étapes 1-9 collectent les critères ; l'étape 10 décide si on active
 * tout de suite (`activateImmediately`) et avec quels responsables le
 * besoin est partagé.
 */
export const recruitmentNeedCreateSchema = z.object({
    // ─── Étape 1 — Métier ──────────────────────────────────────────────────
    title: z.string().trim().min(5).max(200),
    jobFamily: z.enum(HCR_JOB_FAMILIES),
    targetRoles: z
        .array(z.string().trim().min(1).max(120))
        .max(5)
        .default([]),
    // ─── Étape 2 — Contrat ─────────────────────────────────────────────────
    contractTypes: z
        .array(z.enum(RECRUITMENT_CONTRACT_TYPES))
        .min(1, 'Sélectionnez au moins un type de contrat.')
        .max(5),
    // ─── Étape 3 — Dates & urgence ─────────────────────────────────────────
    startDate: z.coerce.date().nullish(),
    urgency: z.enum(RECRUITMENT_URGENCY).default('normal'),
    duration: z.string().trim().max(100).nullish(),
    weeklyHours: z.number().int().min(1).max(48).nullish(),
    availabilityType: z.enum(RECRUITMENT_AVAILABILITY_TYPE).nullish(),
    // ─── Étape 4 — Lieu ────────────────────────────────────────────────────
    location: z.string().trim().max(200).nullish(),
    postalCode: z
        .string()
        .trim()
        .max(10)
        .regex(/^[0-9A-Za-z\s-]*$/, 'Code postal invalide.')
        .nullish(),
    maxCommuteMinutes: z.number().int().min(0).max(180).nullish(),
    // ─── Étape 5 — Horaires et contraintes ─────────────────────────────────
    splitShifts: z.boolean().default(false),
    eveningWork: z.boolean().default(false),
    weekendWork: z.boolean().default(false),
    nightWork: z.boolean().default(false),
    seasonalWork: z.boolean().default(false),
    // ─── Étape 6 — Logement ────────────────────────────────────────────────
    housingOffered: z.boolean().default(false),
    // ─── Étape 7 — Compétences HCR (P2 — vide si HcrSkill pas encore livré) ─
    requiredSkills: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
    // ─── Étape 8 — Langues ─────────────────────────────────────────────────
    // Format : { "fr": "C1", "en": "B2", "es": "A2" } — max 5 langues.
    requiredLanguages: z
        .record(z.string().trim().min(2).max(5), z.enum(CECRL_LEVELS))
        .refine((obj) => Object.keys(obj).length <= 5, {
        message: 'Maximum 5 langues.',
    })
        .nullish(),
    // ─── Étape 9 — Expérience & alternance ─────────────────────────────────
    minimumExperienceMonths: z.number().int().min(0).max(360).nullish(),
    alternanceCompatible: z.boolean().default(false),
    alternanceRhythm: z.string().trim().max(100).nullish(),
    // ─── Étape 10 — Partage et activation ─────────────────────────────────
    shareWithResponsableIds: z
        .array(z.string().min(1).max(64))
        .max(20)
        .default([]),
    activateImmediately: z.boolean().default(false),
});
// ─────────────────────────────────────────────────────────────────────────
// UPDATE — Édition partielle (PATCH)
// ─────────────────────────────────────────────────────────────────────────
/**
 * Payload du PATCH /api/entreprise/besoins/:id. Tous les champs sont
 * optionnels — l'API ne met à jour que ceux fournis. Les transitions de
 * statut passent par les endpoints d'action dédiés (`/activate`, `/pause`,
 * etc.) — `status` n'est PAS modifiable via PATCH (refus 400).
 */
export const recruitmentNeedUpdateSchema = recruitmentNeedCreateSchema
    .partial()
    .omit({ activateImmediately: true })
    .strict();
// ─────────────────────────────────────────────────────────────────────────
// ACTIONS — Transitions de statut & opérations transactionnelles
// ─────────────────────────────────────────────────────────────────────────
/**
 * Payload du POST /api/entreprise/besoins/:id/fill.
 *
 * Effets transactionnels (cf. spec §4.3) :
 *   1. RecruitmentNeed.status = 'filled', filledAt = NOW().
 *   2. Si filledByStudentId fourni : crée un ApplicationPipelineEvent
 *      `contract_signed` (quand ce modèle sera livré, Sprint futur).
 *   3. Si filledByStudentId fourni et candidat dans scope ISTHIA :
 *      Student.status passe à 'placed'.
 *   4. Audit log recruitment_need.fill.
 */
export const recruitmentNeedFillSchema = z.object({
    filledByStudentId: z.string().min(1).max(64).nullish(),
    filledExternally: z.boolean().default(false),
    // Note : le champ `feedback` a été retiré — il était accepté mais
    // jamais persisté (aucune colonne en base). Il sera réintroduit en V1.1
    // avec sa propre table FillFeedback (cf. CdC V3 §3.2).
});
/**
 * Payload du POST /api/entreprise/besoins/:id/convert (→ JobOffer).
 *
 * L'entreprise rédige la description publique (min 50 chars) et choisit
 * la visibilité granulaire (cf. CdC V3 §5.3). Tous les autres champs du
 * JobOffer sont hérités automatiquement du besoin par le backend.
 */
export const recruitmentNeedConvertSchema = z.object({
    description: z.string().trim().min(50).max(5000),
    visibility: z
        .enum(['public', 'restricted_formations', 'restricted_establishment'])
        .default('restricted_establishment'),
    allowedFormationIds: z.array(z.string().min(1).max(64)).max(50).default([]),
    restrictedToEstablishmentId: z.string().min(1).max(64).nullish(),
    expiresAt: z.coerce.date().nullish(),
    formationId: z
        .string()
        .min(1, 'Formation cible obligatoire (annonce vise toujours UNE formation).')
        .max(64),
});
/**
 * Payload du POST /api/entreprise/besoins/:id/share.
 *
 * L'entreprise ajoute un ou plusieurs responsables de formation au tableau
 * `sharedWithResponsableIds`. Les responsables ajoutés verront le besoin
 * dans /responsable/besoins-entreprises et pourront proposer des candidats.
 */
export const recruitmentNeedShareSchema = z.object({
    responsableUserIds: z.array(z.string().min(1).max(64)).min(1).max(20),
});
// ─────────────────────────────────────────────────────────────────────────
// LIST — Filtres de la liste entreprise / responsable
// ─────────────────────────────────────────────────────────────────────────
export const recruitmentNeedListSchema = z.object({
    status: z
        .enum([...RECRUITMENT_NEED_STATUS, 'all'])
        .default('all')
        .optional(),
    urgency: z.enum(RECRUITMENT_URGENCY).optional(),
    jobFamily: z.enum(HCR_JOB_FAMILIES).optional(),
    q: z.string().trim().max(120).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
//# sourceMappingURL=recruitment-need.js.map